import { newId, isServerId } from '../repositories/ids'
import { capture } from '../photos/PhotoStore'
import { tasksRepository } from '../repositories/TasksRepository'
import { taskEvidenceRepository } from '../repositories/TaskEvidenceRepository'
import { IdMappingRepository } from '../repositories/IdMappingRepository'
import { MetaRepository } from '../repositories/MetaRepository'

/**
 * domain/tasks.js — la FACHADA fina del ciclo de vida de tasks (MOD-02).
 *
 * Compone el TasksRepository + TaskEvidenceRepository (F2, escriben data+outbox en UNA tx) y el
 * PhotoStore (06-02) para la evidencia/fotos. Es parte de la UNICA superficie que F6 importa.
 *
 * La fachada NO emite SQL NI toca transporte: solo (a) opcionalmente captura la foto -> ref,
 * (b) delega en el repo, (c) devuelve el client_uuid/result.
 *
 * Las pictures de una task (in-occurrence o standalone) se capturan como FOTOS DE LA TAREA
 * (kind:'task_picture') TRAS crear la task, encolando una fila por foto bajo el client_uuid de la task.
 * NO son evidencia de cumplimiento (kind:'picture'): el camino online las guarda como
 * `task_picture` y la UI las lee de `task_pictures`; mandarlas como evidencia las dejaba invisibles
 * al recargar. Esto coincide con
 * el contrato del tasks-builder (la evidencia es su propio op task.evidence, refs-only) y mantiene
 * el create de la task como un body limpio sin binarios.
 */

/**
 * Columnas REALES de la tabla `tasks` (schema.js §3) que pueden venir del UI. SOLO estas se
 * mapean a la dataRow; cualquier otra clave que mande la UI se enruta al body (nunca a la fila
 * de datos) para no reventar el bind/INSERT.
 */
const TASK_DATA_COLUMNS = new Set([
  'client_uuid', 'server_id', 'occurrence_id', 'title', 'priority', 'role_id', 'assigned_to_id',
  'location_id', 'item_id', 'category', 'category2', 'area_id', 'hierarchy_site_id',
  'hierarchy_subcategory_id', 'end_date', 'state', 'enforce_evidence', 'short_name', 'origin',
  'server_version', 'updated_at', 'created_at'
])

/**
 * Normaliza el objeto de task que mandan createAutoActionPlanTask y OccurrenceTaskCreateDialog al shape EXACTO de
 * columnas de la tabla `tasks`. La UI histórica usa llaves del payload REST viejo que NO existen en
 * el esquema SQLite y reventaban el INSERT (la fila + su outbox jamás se escribían):
 *   - `ocurrence_id` (typo) y `occurrence_id` -> `occurrence_id`
 *   - `assigned_to` -> `assigned_to_id`
 *   - `pictures` se EXTRAE aquí (no es columna): el caller la captura como evidencia tras crear.
 * Las llaves desconocidas (form_id, etc.) NO van a la dataRow; se devuelven en `extra` para que el
 * caller las ponga en el body si las necesita.
 * @returns {{ dataRow: object, picturesBase64: string[], extra: object }}
 */
function normalizeTaskFields (taskRow = {}) {
  const dataRow = {}
  const extra = {}
  let picturesBase64 = []
  for (const [key, value] of Object.entries(taskRow)) {
    if (key === 'ocurrence_id' || key === 'occurrence_id') {
      // Coalesce del typo + el correcto a la única columna real.
      if (value != null) dataRow.occurrence_id = value
    } else if (key === 'assigned_to') {
      if (value != null) dataRow.assigned_to_id = value
    } else if (key === 'pictures') {
      // `pictures` no es columna: se enruta a evidencia (capturar tras crear). Acepta array o null.
      picturesBase64 = Array.isArray(value) ? value.filter(Boolean) : []
    } else if (TASK_DATA_COLUMNS.has(key)) {
      dataRow[key] = value
    } else {
      // Llave desconocida del UI -> body, NUNCA dataRow.
      extra[key] = value
    }
  }
  return { dataRow, picturesBase64, extra }
}

/**
 * Crea una task in-occurrence (EVENT). MOD-02.
 *
 * Acepta el objeto CRUDO que manda la UI (con llaves legacy del payload REST: `ocurrence_id`,
 * `assigned_to`, `pictures`, etc.) y lo WHITELIST+MAPEA al shape de columnas de la tabla `tasks`
 * (ver normalizeTaskFields). Si vienen `pictures` no vacías se capturan como fotos de la tarea (kind:'task_picture')
 * TRAS crear la task, igual que createStandalone. Las llaves desconocidas no se pierden: van al body.
 * `pictureUuids` (opcional): uuid de evidencia por foto, en el MISMO orden que `pictures` ya sin
 * vacíos. Lo genera el caller para ligar cada foto optimista con su op y, tras sincronizar, poder
 * resolver su id de servidor (resolveSyncedTaskPhoto) sin recargar. Sin uuid para un índice: newId().
 * @returns {Promise<string>} client_uuid.
 */
export async function createTask (taskRow = {}, { pictureUuids = [] } = {}) {
  const clientUuid = taskRow.client_uuid || newId()
  const { dataRow, picturesBase64, extra } = normalizeTaskFields(taskRow)
  const row = { ...dataRow, client_uuid: clientUuid }
  // PRE-CAPTURA las fotos a PhotoStore ANTES del create (cierra la ventana de pérdida silenciosa: el
  // binario+photo_meta existen ya, un crash a media lista deja huérfanas detectables por el sweep en
  // vez de fotos que solo vivían en memoria). Mismo espíritu que answers (pre-resuelve el uuid). [review Hugo]
  const precaptured = []
  for (const [index, base64] of picturesBase64.entries()) {
    precaptured.push(await precaptureEvidencePhoto(base64, pictureUuids[index]))
  }
  // `extra` = llaves que NO son columna de `tasks` pero SÍ las lee el backend fromForms (form_id, tag2):
  // van SOLO al body del op create (nunca a la dataRow, que reventaría el UPSERT). Sin esto, un create
  // offline perdía form_id/tag2 -> task sin form ni tag2 en server (divergencia vs el create online).
  await tasksRepository.create(row, extra)
  // Encola la evidencia con las fotos YA capturadas (no re-captura).
  // DEUDA DE PILOTO ACEPTADA [review Hugo #8]: el create (tasksRepository.create = 1 tx atómica) y este
  // loop de evidencia (cada addTaskEvidence = OTRA tx) NO comparten transacción. Si el proceso muere
  // ENTRE el commit del create y el de la 1ª evidencia, la task queda creada sin sus filas de evidencia.
  // Mitigado: la pre-captura de arriba dejó el binario+photo_meta en disco ANTES del create, así que las
  // fotos NO se pierden en silencio — quedan HUÉRFANAS que sweepOrphanPhotos detecta (nota photo_orphan).
  // La ventana es de ms. El fix completo (create + toda la evidencia en UN executeSet) queda como deuda
  // trackeada del piloto; no se hace aquí para no arriesgar regresión en el path crítico de creación.
  for (const p of precaptured) {
    await addTaskEvidence({ task_client_uuid: clientUuid, kind: 'task_picture', precaptured: p })
  }
  return clientUuid
}

/**
 * Crea una task standalone (EVENT), opcionalmente con fotos -> evidencia. MOD-02.
 * Cada foto de picturesBase64 se captura (parentKind:'task_evidence') y se encola como una pieza de
 * foto de la tarea (kind:'task_picture') bajo el client_uuid de la task tras crearla.
 * @returns {Promise<string>} client_uuid de la task.
 */
export async function createStandalone ({ picturesBase64 = [], ...taskFields } = {}) {
  const clientUuid = taskFields.client_uuid || newId()
  const taskRow = { ...taskFields, client_uuid: clientUuid }
  // PRE-CAPTURA antes del create (cierra la ventana de pérdida silenciosa — ver createTask). [review Hugo]
  const precaptured = []
  for (const base64 of picturesBase64) {
    if (!base64) continue
    precaptured.push(await precaptureEvidencePhoto(base64))
  }
  const result = await tasksRepository.createStandalone(taskRow)
  for (const p of precaptured) {
    await addTaskEvidence({ task_client_uuid: clientUuid, kind: 'task_picture', precaptured: p })
  }
  return result
}

/**
 * Edita campos de una task (EVENT, identidad de edicion unica por el repo). MOD-02. Sin foto.
 * @returns {Promise<string>} client_uuid.
 */
export function editTask (clientUuid, dataChanges = {}, bodyChanges = dataChanges) {
  // dataChanges = SOLO columnas reales de `tasks` (para el UPSERT); bodyChanges = claves del payload
  // REST (para el builder). El caller (saveLinkedTaskFields de la store de occurrences) separa ambos; ver PR review #4.
  return tasksRepository.updateFields(clientUuid, dataChanges, bodyChanges)
}

/**
 * Cambia el estado de una task (completar/suspender), dedup-por-estado. MOD-02. Sin foto.
 * @returns {Promise<string>} client_uuid.
 */
export function changeTaskStatus (clientUuid, state, fields = {}) {
  return tasksRepository.changeStatus(clientUuid, state, fields)
}

/**
 * Pre-captura una foto de evidencia a PhotoStore (crea photo_meta) SIN encolar el op todavía, y
 * devuelve el descriptor listo para addTaskEvidence. Se usa para CERRAR la ventana de pérdida
 * silenciosa en createTask/createStandalone: el binario+photo_meta existen ANTES del create, así que
 * un crash a media lista deja HUÉRFANAS DETECTABLES (sweepOrphanPhotos) en vez de fotos que solo
 * vivían en memoria (base64) y se perdían sin rastro. [review Hugo]
 * @returns {Promise<{uuid, image_ref, local_path}>}
 */
async function precaptureEvidencePhoto (photoBase64, evUuid = newId()) {
  const captured = await capture(photoBase64, { parentUuid: evUuid, parentKind: 'task_evidence', itemId: null })
  return { uuid: evUuid, image_ref: captured.image_ref, local_path: captured.local_path }
}

/**
 * Adjunta una pieza de evidencia a una task (EVENT). MOD-02.
 * Si kind es picture/task_picture/file y hay photoBase64 -> PhotoStore.capture -> {image_ref, local_path} -> repo
 * (que escribe data+outbox atomico). Evidencia solo-comentario: sin captura.
 * `precaptured` (opcional): descriptor de precaptureEvidencePhoto -> se REUSA su uuid/refs y NO se
 * re-captura (el binario ya está en PhotoStore).
 * @returns {Promise<string>} uuid de la evidencia.
 */
export async function addTaskEvidence ({
  task_client_uuid: taskClientUuid,
  kind,
  photoBase64,
  comment,
  filename,
  precaptured
} = {}) {
  // El uuid de la evidencia = client_uuid del op de outbox (task.evidence) que sube la foto. Se
  // resuelve ANTES de capturar para sembrar parent_uuid correcto (FIX purga locked [06-02]): el
  // op vive bajo evRow.uuid, NO bajo task_client_uuid, asi que la purga debe cruzar por evRow.uuid.
  const evUuid = precaptured?.uuid || newId()
  let imageRef = precaptured?.image_ref
  let localPath = precaptured?.local_path
  const isBinary = kind === 'picture' || kind === 'task_picture' || kind === 'file'
  if (isBinary && photoBase64 && !precaptured) {
    const captured = await capture(photoBase64, {
      parentUuid: evUuid,
      parentKind: 'task_evidence',
      itemId: null
    })
    imageRef = captured.image_ref
    localPath = captured.local_path
  }
  const evRow = {
    uuid: evUuid,
    task_client_uuid: taskClientUuid,
    kind,
    image_ref: imageRef,
    local_path: localPath,
    comment,
    filename,
    created_at: new Date().toISOString()
  }
  return taskEvidenceRepository.addEvidence(evRow)
}

/**
 * Ids de servidor de una foto de tarea ya adjunta, para editarla o borrarla por updateFildImages.
 *
 * Una foto agregada offline se pinta optimista con `id: null` y su `ev_uuid` (uuid del op
 * task.evidence). Al sincronizar, el handler guarda id_mapping('task_evidence', ev_uuid -> answer id),
 * pero la foto en memoria sigue con `id: null` hasta recargar la ocurrencia. Esto resuelve ese id
 * (y el server_id de la tarea si se creó offline) en el momento de editar/borrar, sin recargar.
 *
 * @param {{ taskId: string|number, picture: { id?: number|null, ev_uuid?: string } }} args
 * @returns {Promise<{ taskId: number, answerId: number }|null>} null = aún no sincroniza (o sin base).
 */
export async function resolveSyncedTaskPhoto ({ taskId, picture } = {}) {
  if (!picture) return null
  try {
    let answerId = picture.id ?? null
    if (answerId == null && picture.ev_uuid) {
      answerId = await IdMappingRepository.serverIdFor('task_evidence', picture.ev_uuid)
    }
    const serverTaskId = isServerId(taskId)
      ? Number(taskId)
      : await IdMappingRepository.serverIdFor('task', String(taskId))

    // Solo ids REALES de servidor (enteros positivos) salen a updateFildImages: nunca un client_uuid
    // ni un valor raro como id/PK (misma regla que FIX 9 / findEditableTask). Si no, "aún no sincroniza".
    if (!isServerId(answerId) || !isServerId(serverTaskId)) return null

    return { taskId: Number(serverTaskId), answerId: Number(answerId) }
  } catch {
    // Base cerrada/no abierta (ISS-1009): sin id_mapping consultable -> se trata como no sincronizada.
    return null
  }
}

/**
 * Espera ACOTADA a que una foto de tarea recién agregada sincronice, para poder editarla/borrarla sin
 * recargar. El nudge de sync coalesce los guardados (debounce de 10 s, sync-nudge.js), así que tocar
 * editar justo después de agregar la foto caía antes de que subiera.
 *
 * `kick` (p.ej. syncNow()) se dispara UNA vez y NO se espera: un tick completo incluye el pull de
 * catálogos, que puede tardar minutos, así que esperarlo rompería el tope. La espera se acota solo
 * por el polling local de id_mapping. Se inyecta para no acoplar la fachada al worker de sync.
 *
 * @param {{ taskId: string|number, picture: object, kick?: function, timeoutMs?: number, pollMs?: number }} args
 * @returns {Promise<{ taskId: number, answerId: number }|null>} null = no sincronizó dentro del tope.
 */
export async function waitForSyncedTaskPhoto ({ taskId, picture, kick, timeoutMs = 10000, pollMs = 500 } = {}) {
  if (typeof kick === 'function') {
    // best-effort: ni un kick que truena ni uno que tarda deben afectar la espera.
    Promise.resolve().then(kick).catch(() => {})
  }
  const deadline = Date.now() + timeoutMs
  while (Date.now() < deadline) {
    const ids = await resolveSyncedTaskPhoto({ taskId, picture })
    if (ids) return ids
    await new Promise(resolve => setTimeout(resolve, pollMs))
  }
  return resolveSyncedTaskPhoto({ taskId, picture })
}

/**
 * Borra una task in-occurrence (EVENT). MOD-02.
 * @returns {Promise<string>} client_uuid.
 */
export function deleteTask (clientUuid) {
  return tasksRepository.remove(clientUuid)
}

/**
 * Borra una task standalone (EVENT). MOD-02.
 * @returns {Promise<string>} client_uuid.
 */
export function deleteStandalone (clientUuid) {
  return tasksRepository.removeStandalone(clientUuid)
}

/**
 * FIX 9 — versión para el CIERRE ONLINE LEGACY de resolveTasksEditingIds (el remap del outbox vive
 * en sync/modules/occurrences.js y corre al drenar). El camino online (flag apagado a media sesión)
 * manda tasksEditing directo a saveEditingTasks, pero las tasks creadas mientras el flag estuvo
 * encendido conservan id = client_uuid en el store aunque su task.create ya haya drenado: sin este
 * remap, el backend (#1831) las CREA de nuevo (duplicado) en vez de actualizar la existente.
 * Solo sustituye ids con mapping ya sembrado; un uuid sin mapping viaja tal cual (misma red de
 * seguridad: #1831 lo crea completo — aquí no hay outbox drenando, así que no hay a quién esperar).
 * El caller debe verificar storageReady ANTES: sin base abierta no hay id_mapping que consultar.
 * @param {Array<object>} tasksEditing
 * @returns {Promise<Array<object>>} mismas tasks, con id remapeado donde hubo mapping.
 */
export async function remapTasksEditingServerIds (tasksEditing = []) {
  const resolved = []
  for (const task of tasksEditing) {
    if (!task || isServerId(task.id)) {
      resolved.push(task)
      continue
    }
    const clientUuid = String(task.client_uuid || task.id)
    const serverId = await IdMappingRepository.serverIdFor('task', clientUuid)
    resolved.push(serverId != null ? { ...task, id: serverId } : task)
  }
  return resolved
}

/**
 * TKT-1975 — asignación inicial PENDIENTE. Una tarea de plan de acción creada SIN red no puede elegir
 * usuario (la lista de usuarios del rol solo existe online), así que se marca aquí y la tarjeta la
 * resuelve UNA vez cuando vuelve la red, también tras reabrir la ocurrencia (por eso es durable y
 * no vive solo en memoria). Elegir usuario o rol a mano la limpia: desde ahí decide el usuario.
 */
const PENDING_ASSIGNMENT_PREFIX = 'tassign:'

export function markAssignmentPending (clientUuid) {
  return MetaRepository.put(MetaRepository.taskAssignmentPendingKey(clientUuid))
}

/**
 * client_uuid de la marca pendiente que corresponde a `taskId`, o null. `taskId` es el client_uuid
 * (tarea optimista, aún en memoria) o el id de servidor (tarea recargada tras sincronizar). Para el
 * segundo caso se recorren las marcas y se compara por serverIdFor: un mismo server_id puede tener
 * más de un client_uuid en id_mapping (el original y el srv:task:<id> del down-sync).
 */
async function pendingAssignmentUuidFor (taskId) {
  if (taskId == null) return null
  if (!isServerId(taskId)) {
    const clientUuid = String(taskId)
    return (await MetaRepository.has(MetaRepository.taskAssignmentPendingKey(clientUuid))) ? clientUuid : null
  }
  const keys = await MetaRepository.keysWithPrefix(PENDING_ASSIGNMENT_PREFIX)
  for (const key of keys) {
    const clientUuid = key.slice(PENDING_ASSIGNMENT_PREFIX.length)
    const serverId = await IdMappingRepository.serverIdFor('task', clientUuid)
    if (serverId != null && Number(serverId) === Number(taskId)) return clientUuid
  }
  return null
}

export async function isAssignmentPending (taskId) {
  return (await pendingAssignmentUuidFor(taskId)) !== null
}

export async function clearAssignmentPending (taskId) {
  const clientUuid = await pendingAssignmentUuidFor(taskId)
  if (clientUuid) await MetaRepository.del(MetaRepository.taskAssignmentPendingKey(clientUuid))
}
